import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import NoRecordFound from '../../../components/common/no-record-found';
import { FaPlus } from 'react-icons/fa';

const ROWS_PER_PAGE = 34;

const DispatchList = () => {
  const navigate = useNavigate();
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Helper to format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

const fetchDispatches = async () => {
  setLoading(true);
  setError(null);
  try {
    const params = new URLSearchParams({
      page,
      limit: ROWS_PER_PAGE,
      search: debouncedSearch.trim()
    });
    const response = await axios.get(
      `${API_BASE_URL}/api/sales/dispatch-entry/list?${params.toString()}`,
      {
        withCredentials: true  // This is the Axios equivalent of credentials: 'include'
      }
    );
    setDispatches(response.data.data || []);
    setTotal(response.data.total || 0);
  } catch (error) {
    setError('Failed to load data');
    console.error('Failed to fetch dispatch entries:', error);
  } finally {
    setLoading(false);
  }
};
  useEffect(() => {
    fetchDispatches();
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  // Pagination
  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Dispatch Records</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={handleSearchChange}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            className="flex items-center gap-1 bg-[#3899cf] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
            onClick={() => navigate('/dispatch/new')}
          >
            <FaPlus />
            Add New
          </button>
        </div>
      </div>
      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : dispatches.length === 0 ? (
        <NoRecordFound message="No dispatch records found." />
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-medium">#</th>
                <th className="px-3 py-2 text-left font-medium">Dispatch Date</th>
                <th className="px-3 py-2 text-left font-medium">Sales Order ID</th>
                <th className="px-3 py-2 text-left font-medium">DO ID</th>
                <th className="px-3 py-2 text-left font-medium">Vehicle No</th>
                <th className="px-3 py-2 text-left font-medium">Qty (MT)</th>
                <th className="px-3 py-2 text-left font-medium">Invoice No</th>
                <th className="px-3 py-2 text-left font-medium">Amount (INR)</th>
              </tr>
            </thead>
            <tbody>
              {dispatches.map((item, idx) => (
                <tr key={item.dispatch_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-1">{idx + 1}</td>
                  <td className="px-3 py-1">{formatDate(item.dispatch_date)}</td>
                  <td className="px-3 py-1">{item.sales_order_id}</td>
                  <td className="px-3 py-1">{item.do_id}</td>
                  <td className="px-3 py-1">{item.vehicle_no}</td>
                  <td className="px-3 py-1">{item.lifted_quantity_mt}</td>
                  <td className="px-3 py-1">{item.invoice_no || '-'}</td>
                  <td className="px-3 py-1">{item.invoice_amount || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Pagination Controls */}
      {total > ROWS_PER_PAGE && (
        <div className="flex justify-end items-center gap-2 mt-3 text-xs">
          <button
            onClick={handlePrevPage}
            disabled={page === 1}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default DispatchList;